import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, Mail, Lock, LogIn, AlertTriangle, ChevronRight, Crown, Users, Shield, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionExpired = searchParams.get("expired") === "true";
  // A deliberate logout (see authService.logout) also lands here with
  // ?unauthorized=true because of the ProtectedRoute guard's own redirect —
  // the flag tells us to skip the "you must log in" banner in that case.
  // Read via lazy useState (not directly in the render body) and clear it in
  // an effect so the flag survives React 18 StrictMode's double-render.
  const [justLoggedOut] = useState(() => sessionStorage.getItem("justLoggedOut") === "1");
  useEffect(() => {
    if (justLoggedOut) sessionStorage.removeItem("justLoggedOut");
  }, [justLoggedOut]);
  const unauthorized = searchParams.get("unauthorized") === "true" && !justLoggedOut;
  const registered = searchParams.get("registered") === "true";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err: any) {
      const message = err?.response?.data?.message || "";
      const status = err?.response?.status;
      if (message.includes("ACCOUNT_DEACTIVATED")) {
        navigate(`/account-deactivated?email=${encodeURIComponent(email)}`);
      } else if (status === 401) {
        setError("Email o contraseña incorrectos. Verificá tus datos e intentá nuevamente.");
      } else if (!status || status >= 500) {
        setError("No se pudo conectar con el servidor. Intentá de nuevo en unos momentos.");
      } else {
        setError(message || "Ocurrió un error al iniciar sesión. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel — Branding */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
                               radial-gradient(circle at 75% 75%, rgba(99, 102, 241, 0.2) 0%, transparent 50%)`,
            }}
          />
        </div>

        {/* Floating geometric shapes */}
        <div className="absolute w-32 h-32 border top-20 left-16 border-indigo-500/20 rounded-2xl rotate-12 animate-pulse" />
        <div className="absolute w-24 h-24 border bottom-32 right-20 border-indigo-400/15 rounded-xl -rotate-12" />
        <div className="absolute w-16 h-16 rotate-45 rounded-lg top-1/2 left-1/3 bg-indigo-500/10" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          <div className="flex items-center gap-4 mb-10">
            <div className="flex items-center justify-center border w-14 h-14 rounded-2xl bg-indigo-500/20 backdrop-blur-sm border-indigo-500/30">
              <Building2 className="text-indigo-400 w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white">PruneStone</h1>
              <p className="text-sm font-medium text-indigo-300/80">Construction Management</p>
            </div>
          </div>

          <h2 className="mb-6 text-4xl font-bold leading-tight text-white xl:text-5xl">
            Gestión integral<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              para tu constructora
            </span>
          </h2>

          <p className="max-w-md mb-12 text-lg leading-relaxed text-slate-400">
            Controla proyectos, finanzas, compras y ventas desde un solo lugar. Todo tu equipo conectado en tiempo real.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3">
            {['Proyectos', 'Finanzas', 'Compras', 'Ventas'].map((feature) => (
              <span
                key={feature}
                className="px-4 py-2 text-sm font-medium text-indigo-300 border rounded-full bg-indigo-500/10 backdrop-blur-sm border-indigo-500/20"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — Login form */}
      <div className="flex items-center justify-center flex-1 px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-10 lg:hidden">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">PruneStone</h1>
          </div>

          {/* Session expired / unauthorized alerts */}
              {sessionExpired && (
                <div className="flex items-center gap-3 px-4 py-3 mb-6 border rounded-xl bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="flex-shrink-0 w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    Tu sesión ha expirado. Por favor, iniciá sesión nuevamente.
                  </p>
                </div>
              )}

              {unauthorized && (
                <div className="flex items-center gap-3 px-4 py-3 mb-6 border border-red-200 rounded-xl bg-red-50 dark:bg-red-950/30 dark:border-red-800">
                  <AlertTriangle className="flex-shrink-0 w-5 h-5 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-800 dark:text-red-300">
                    Necesitás iniciar sesión para acceder a esa página.
                  </p>
                </div>
              )}

              {registered && (
                <div className="flex items-center gap-3 px-4 py-3 mb-6 border rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
                  <AlertTriangle className="flex-shrink-0 w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <p className="text-sm text-emerald-800 dark:text-emerald-300">
                    Cuenta creada con éxito. Iniciá sesión para continuar.
                  </p>
                </div>
              )}

              {/* Header */}
              <div className="mb-8">
                <h2 className="mb-2 text-2xl font-bold text-foreground">Bienvenido</h2>
                <p className="text-muted-foreground">
                  Ingresá tus credenciales para acceder al sistema
                </p>
              </div>

              {/* Form */}
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      placeholder="Mail"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full py-3 pl-10 pr-4 text-sm transition-all duration-200 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full py-3 pl-10 pr-10 text-sm transition-all duration-200 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 border border-red-200 rounded-xl bg-red-50 dark:bg-red-950/30 dark:border-red-800">
                    <AlertTriangle className="flex-shrink-0 w-4 h-4 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-semibold text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 rounded-xl shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-background"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Iniciar Sesión
                    </>
                  )}
                </button>
              </form>

          {/* Register CTA */}
          <div className="mt-8 space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3 font-medium tracking-wider bg-background text-muted-foreground">
                  ¿Primera vez aquí?
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3 border rounded-xl border-border bg-muted/20">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center flex-shrink-0 bg-indigo-100 rounded-lg w-9 h-9 dark:bg-indigo-900/30">
                  <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Registrar nueva empresa</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Creá un espacio de trabajo independiente para tu constructora. Serás el administrador principal.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { icon: Crown, label: 'Sos el admin' },
                  { icon: Users, label: 'Invitás a tu equipo' },
                  { icon: Shield, label: 'Datos aislados' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-1 py-2 rounded-lg bg-background/60">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-2 px-3 py-2 border rounded-lg bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  ¿Sos empleado invitado? <strong>No uses este registro.</strong> Buscá el mail de invitación en tu casilla.
                </p>
              </div>

              <Link
                to="/register"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all duration-200"
              >
                Crear nuevo espacio de trabajo
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-10 text-xs text-center text-muted-foreground">
            PruneStone &copy; {new Date().getFullYear()} — Sistema de Gestión para Constructoras
          </p>
        </div>
      </div>
    </div>
  );
}